import { useCallback, useRef, useState } from 'react'
import type { FormEvent } from 'react'
import type { SendCrewInvite } from '@/modules/auth/use-cases'
import { reportError } from '@/shared/infra/observability'
import { useSubmitGuard } from '@/shared/ui/hooks'

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export const CREW_INVITE_NAME_REQUIRED_MESSAGE = 'Enter a first and last name.'
export const CREW_INVITE_EMAIL_INVALID_MESSAGE = 'Enter a valid email.'
export const CREW_INVITE_EMAIL_DUPLICATE_MESSAGE = 'This email is already in the list.'
export const CREW_INVITE_EMPTY_MESSAGE = 'Add at least one hunter, or tap Skip.'
export const CREW_INVITE_FAILED_MESSAGE = "We couldn't invite this hunter. Please try again."

export type CrewInviteField = 'firstName' | 'lastName' | 'email'

export type CrewInviteRow = {
  id: number
  firstName: string
  lastName: string
  email: string
  error: string
}

export type UseCrewInviteStepProps = {
  orgId: string
  onSendCrewInvite: SendCrewInvite
  onDone: () => void
}

export type UseCrewInviteStepResult = {
  rows: CrewInviteRow[]
  formError: string
  sentCount: number
  isSending: boolean
  updateRow: (id: number, field: CrewInviteField, value: string) => void
  addRow: () => void
  removeRow: (id: number) => void
  handleSend: (event?: FormEvent) => Promise<void>
  handleSkip: () => void
}

function emptyRow(id: number): CrewInviteRow {
  return { id, firstName: '', lastName: '', email: '', error: '' }
}

function isBlank(row: CrewInviteRow): boolean {
  return !row.firstName.trim() && !row.lastName.trim() && !row.email.trim()
}

function validateRows(rows: CrewInviteRow[]): CrewInviteRow[] {
  const seen = new Set<string>()
  return rows.map((row) => {
    const email = row.email.trim().toLowerCase()
    let error = ''
    if (!row.firstName.trim() || !row.lastName.trim()) error = CREW_INVITE_NAME_REQUIRED_MESSAGE
    else if (!EMAIL_PATTERN.test(email)) error = CREW_INVITE_EMAIL_INVALID_MESSAGE
    else if (seen.has(email)) error = CREW_INVITE_EMAIL_DUPLICATE_MESSAGE
    seen.add(email)
    return { ...row, error }
  })
}

export function useCrewInviteStep({
  orgId,
  onSendCrewInvite,
  onDone,
}: UseCrewInviteStepProps): UseCrewInviteStepResult {
  const { isSubmitting, runGuarded } = useSubmitGuard()
  const nextIdRef = useRef(1)
  const [rows, setRows] = useState<CrewInviteRow[]>(() => [emptyRow(0)])
  const [formError, setFormError] = useState('')
  const [sentCount, setSentCount] = useState(0)

  const updateRow = useCallback((id: number, field: CrewInviteField, value: string) => {
    setFormError('')
    setRows((current) =>
      current.map((row) => (row.id === id ? { ...row, [field]: value, error: '' } : row)),
    )
  }, [])

  const addRow = useCallback(() => {
    const id = nextIdRef.current
    nextIdRef.current += 1
    setRows((current) => [...current, emptyRow(id)])
  }, [])

  const removeRow = useCallback((id: number) => {
    setRows((current) => {
      const remaining = current.filter((row) => row.id !== id)
      if (remaining.length > 0) return remaining
      const fresh = nextIdRef.current
      nextIdRef.current += 1
      return [emptyRow(fresh)]
    })
  }, [])

  const handleSend = useCallback(
    async (event?: FormEvent) => {
      event?.preventDefault()
      await runGuarded(async () => {
        const filled = rows.filter((row) => !isBlank(row))
        if (filled.length === 0) {
          setFormError(CREW_INVITE_EMPTY_MESSAGE)
          return
        }
        const validated = validateRows(filled)
        if (validated.some((row) => row.error)) {
          setRows(validated)
          return
        }
        setFormError('')
        const results = await Promise.allSettled(
          validated.map((row) =>
            onSendCrewInvite({
              orgId,
              firstName: row.firstName.trim(),
              lastName: row.lastName.trim(),
              email: row.email.trim().toLowerCase(),
            }),
          ),
        )
        const failed: CrewInviteRow[] = []
        results.forEach((result, index) => {
          if (result.status === 'rejected') {
            const error = reportError(result.reason, { fallbackMessage: CREW_INVITE_FAILED_MESSAGE })
            failed.push({ ...validated[index], error })
          }
        })
        setSentCount((count) => count + validated.length - failed.length)
        if (failed.length === 0) {
          onDone()
          return
        }
        setRows(failed)
      })
    },
    [runGuarded, rows, orgId, onSendCrewInvite, onDone],
  )

  const handleSkip = useCallback(() => {
    if (isSubmitting) return
    onDone()
  }, [isSubmitting, onDone])

  return {
    rows,
    formError,
    sentCount,
    isSending: isSubmitting,
    updateRow,
    addRow,
    removeRow,
    handleSend,
    handleSkip,
  }
}
